/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import util from '../../../../share/util/utils';
import svgButton from '../../../../share/components/vue/SVGButton.vue';
import { apiErrorDefaultAction } from "../../../../share/util/api/errorAction";
import { PHASE } from './constants';
import { read } from './read';
import { upload } from './upload';
import { terminate } from './terminate';
import { dispose } from './dispose';

// チェックボックスのチェック制限数
const LIMIT_NUM = 3;

export default {
  name: 'requirement148',
  data() {
    return {
      modal_requirement_148: false,
      systemId: '',
      isLoading: true,
      phase: PHASE.INDEX_ACQUISITION,
      isSession: false,
      displayedIndexItems: [],
      selectedDate: {
        startDate: null
      },
      requestId: null,
      dataName: null,
      resultItems: [],
      columns: [
        { name: 'check', label: '' },
        { name: 'number', label: this.i18nWsKey + '.table_column_number' },
        { name: 'period', label: this.i18nWsKey + '.table_column_period' }
      ],
      isCanceled: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.vehicle.vehicleInfo,
    }),

    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.DATA_NON_EXISTENCE:
        case PHASE.ACQUISITION_FAILED:
        case PHASE.COMMON_ERROR:
          return 'danger';
        case PHASE.DATA_RESULTS_DISPLAY:
          return 'success';
        case PHASE.INDEX_ACQUISITION:
        case PHASE.INDEX_DISPLAY:
        case PHASE.DATA_ACQUISITION:
        default:
          return 'secondary';
      }
    },

    /**
     * ヘッダータイトル
     */
    headerTitle() {
      // Index表示
      const confirmationTitle = 'message.common_title_confirmation';
      // Index取得, ログデータ取得
      const executionTitle = 'individual.common_title_execution';
      // ログデータ有り表示
      const successTitle = 'message.common_title_success';
      // ログデータなし表示, エラー, 共通エラー
      const errorTitle = 'message.common_title_error';
      // タイトルマップ
      const titleMap = {
        [PHASE.INDEX_ACQUISITION]: executionTitle,
        [PHASE.INDEX_DISPLAY]: confirmationTitle,
        [PHASE.DATA_ACQUISITION]: executionTitle,
        [PHASE.DATA_RESULTS_DISPLAY]: successTitle,
        [PHASE.DATA_NON_EXISTENCE]: errorTitle,
        [PHASE.ACQUISITION_FAILED]: errorTitle,
        [PHASE.COMMON_ERROR]: errorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * ダイアログ文言
     */
    workSupportMessage() {
      const messageMap = {
        [PHASE.INDEX_ACQUISITION]: `${this.i18nWsKey}.REQ_WS_148_TXT_1`,
        [PHASE.DATA_ACQUISITION]: `${this.i18nWsKey}.REQ_WS_148_TXT_2`,
        [PHASE.DATA_NON_EXISTENCE]: `${this.i18nWsKey}.REQ_WS_148_TXT_3`,
        [PHASE.DATA_RESULTS_DISPLAY]: `${this.i18nWsKey}.REQ_WS_148_TXT_4`,
        [PHASE.ACQUISITION_FAILED]: `${this.i18nWsKey}.REQ_WS_148_TXT_5`,
        [PHASE.COMMON_ERROR]: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },

    /**
     * Index表示Item
     */
    showItems() {
      if (!this.displayedIndexItems) return [];
      return this.displayedIndexItems.map(item => {
        return {
          isChecked: item.isChecked,
          number: item.number,
          period: `${item.period_start} ~ ${item.period_end}`,
        };
      });
    },

    /**
     * 取得ボタン非活性状態
     */
    isDisabledAcquisition() {
      return this.showItems.filter(item => item.isChecked).length === 0;
    },

    /**
     * 選択上限判定
     */
    isSelectionLimit() {
      return this.showItems.filter(item => item.isChecked).length >= LIMIT_NUM;
    },

    /**
     * weekName
     */
    getDaysOnLocale() {
      return util.getDaysOnLocale();
    },
  },

  components: {
    'svg-button': svgButton,
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期処理、Index読出し */
    async init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_148 = true;
      await read(this);
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_148 = false;
    },

    /** 本機能独自エラー画面の表示 */
    async showIndividualError() {
      // NonDefaultSession遷移が未実行の場合はDispose処理
      if (this.isSession) {
        await terminate(this, PHASE.ACQUISITION_FAILED);
      } else {
        await dispose(this, PHASE.ACQUISITION_FAILED);
      }
    },

    /** その他エラーの表示 */
    async showUnexpectedError() {
      // NonDefaultSession遷移が未実行の場合はDispose処理
      if (this.isSession) {
        await terminate(this, PHASE.COMMON_ERROR);
      } else {
        await dispose(this, PHASE.COMMON_ERROR);
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    async validateErrorResult(result) {
      await apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        async (result) => { result; await this.showUnexpectedError(); },
      );
    },


    /**
     * 共通の[キャンセル]ボタンクリック処理
     */
    async onCancelClicked() {
      switch (this.phase) {
        case PHASE.INDEX_DISPLAY:
          await (this.isSession ? terminate : dispose)(this);
          this.closeWizard();
          break;
        case PHASE.DATA_ACQUISITION:
          this.isCanceled = true;
          break;
        default:
          // noop
          break;
      }
    },

    /**
     * 共通の[OK], [取得]ボタンクリック処理
     */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.INDEX_DISPLAY:
          await upload(this);
          break;
        case PHASE.DATA_RESULTS_DISPLAY:
        case PHASE.DATA_NON_EXISTENCE:
        case PHASE.ACQUISITION_FAILED:
        case PHASE.COMMON_ERROR:
          this.closeWizard();
          break;
        default:
          // noop
          break;
      }
    },

    /** 
    * チェックボックス変更イベント
    * @param selectedItem 変更対象のIndex情報
    */
    checkboxChanged(selectedItem) {
      const idx = this.displayedIndexItems.findIndex(item => item.number === selectedItem.number);
      if (idx >= 0) {
        this.displayedIndexItems[idx].isChecked = !this.displayedIndexItems[idx].isChecked;
      }
    },

    /** 
    * テーブル列に適用するスタイルを指定
    * @param number index
    * @returns style
    */
    getTdStyle(index) {
      return { 'table-row-stripe': (index % 2 === 0) && true };
    },

    /** チェックボックスのカーソルスタイルを指定 */
    getCursorStyle(item) {
      return this.isSelectionLimit && !item.isChecked ? 'cursor-not-allowed' : 'cursor-pointer';
    },

    /**
     * Index一覧の自動スクロール
     */
    scrollItem() {  
      if (Object.prototype.toString.call(this.selectedDate?.startDate) === '[object Date]') {
        // 基準時刻を00:00:00に設定
        this.selectedDate.startDate.setHours(0, 0, 0);
        // 選択日時との差分が少ないデータのインデックスを取得
        let targetIndex = -1;
        let minDiff = Infinity;
        this.displayedIndexItems.forEach((item, index) => {
          const diff = Math.abs(new Date(item.period_start) - new Date(this.selectedDate.startDate));
          if (diff < minDiff) {
            minDiff = diff;
            targetIndex = index;
          }
        });

        if (targetIndex > -1) {
          this.$nextTick(() => {
            if (this.$refs.targetRow && this.$refs.targetRow[targetIndex]) {
              // 自動スクロール対象がヘッダー行の裏に隠れるためマージンを設定
              this.$refs.targetRow[targetIndex].style.scrollMarginTop = '34px';
              // 自動スクロール
              this.$refs.targetRow[targetIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
              // 自動スクロール後マージンを削除
              this.$refs.targetRow[targetIndex].style.scrollMarginTop = '';
            }
          });
        }
      }
    }
  },

  async mounted() {
    await this.init();
  },
};